/// <reference types="@types/google.maps" />
import { DragAndDropFileUploader } from "@/components/drag-and-drop/DragAndDropFileUploader";
import type { PdfValidationState, UploadProgress } from "@/components/estimation-details/upload/hooks/useUploadEstimationFiles";
import { useUploadEstimationFiles } from "@/components/estimation-details/upload/hooks/useUploadEstimationFiles";
import { Button } from "@/components/ui/button";
import { EstimateFile } from "@/enums/estimate-file";
import type { AddNoteBody, Estimation } from "@/interfaces/estimation.interface";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "@/analytics";
import { X, FileText, Link, Paperclip } from "lucide-react";
import { UploadedFileList } from "./UploadedFileList";
import { UploadedFile } from "./UploadedFile";
import estimateProvider from "@/providers/estimate.provider";
import type { File as EstimateFileType } from "@/interfaces/estimation.interface";
import { appSelector } from "@/redux/selectors/app.selector";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useSelector } from "react-redux";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useGetStyles } from "@/hooks/useGetFontSize";
import { RebuildHeader } from "@/components/layout/RebuildHeader";
import { EstimateType } from "@/enums/estimate-type";
import { Conversation } from "./voice/Conversation";
import { UploadedFileProgress } from "./UploadedFileProgress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useConversation } from "@elevenlabs/react";
import AddressAutoComplete from "@/components/AddressAutoComplete";
import { EstimateTypeDropdown, getNoteTypeFromEstimateType } from "./EstimateTypeDropdown";
import { TPASelect } from "./TPASelect";
import { CarrierSelect } from "./CarrierSelect";
import { RateCardSelect } from "./RateCardSelect";
import { useGetRateCardsQuery } from "@/hooks/queries/rate-cards";
import type { RateCard } from "@rebuild/shared";
import { useDebounceState } from "@/hooks/useDebounceState";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { mobileAddressImportForOrganizations, mobileExcelUploadForOrganizations } from "@/config/uploadScreen";
import { NoteType } from "@rebuild/shared";
import { clearScopeNoteDraft, NEW_ESTIMATE_SCOPE_NOTE_DRAFT_ID, readScopeNoteDraft, writeScopeNoteDraft } from "./scopeNoteDraftStorage";

// only maintain state for new scope note object and new link
export type EstimateUploadState = Partial<
  Omit<Estimation, "scopeNoteObjects" | "links"> & {
    scopeNoteText: string;
    scopeNoteWorkAfterHours: boolean;
    link: string;
  }
>;

type UploadValidationSummary = {
  hasValidationInProgress: boolean;
  hasUnknownFormat: boolean;
  hasMixedKnownFormats: boolean;
};

type PdfValidationEntry = NonNullable<PdfValidationState[string]>;

const getPdfValidationSummary = (uploadedFiles: EstimateFileType[], pdfValidationState: PdfValidationState): UploadValidationSummary => {
  const pdfValidationEntries = uploadedFiles
    .filter((file) => {
      const lowerOriginalName = file.originalname?.toLowerCase() ?? "";
      const lowerRemotePath = file.remotePath?.toLowerCase() ?? "";
      const lowerMime = file.mime?.toLowerCase() ?? "";
      return !!file.uploadId && (lowerOriginalName.endsWith(".pdf") || lowerRemotePath.endsWith(".pdf") || lowerMime.includes("pdf"));
    })
    .map((file) => (file.uploadId ? pdfValidationState[file.uploadId] : undefined))
    .filter((validation): validation is PdfValidationEntry => validation !== undefined);

  const hasValidationInProgress = pdfValidationEntries.some((validation) => validation.status === "VALIDATING");
  const hasUnknownFormat = pdfValidationEntries.some((validation) => validation.status === "ERROR" || validation.format === "UNKNOWN");
  const knownFormats = new Set(
    pdfValidationEntries
      .map((validation) => validation.format)
      .filter((format): format is Exclude<PdfValidationEntry["format"], undefined | "UNKNOWN"> => !!format && format !== "UNKNOWN"),
  );
  const hasMixedKnownFormats = knownFormats.size > 1;

  return {
    hasValidationInProgress,
    hasUnknownFormat,
    hasMixedKnownFormats,
  };
};

export const UploadEstimationData = ({
  estimation,
  onSubmit: _onSubmit,
  onClose,
  submitText,
  show,
}: {
  estimation: Estimation | undefined;
  onSubmit: (estimateUploadState: EstimateUploadState) => Promise<void>;
  onClose: () => void;
  submitText: string;
  show: boolean;
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const user = useSelector(appSelector.user);
  const newEstimateDraftParams = useMemo(
    () => ({
      userId: user?.lassoUserId,
      organizationId: user?.organizationId,
      draftId: NEW_ESTIMATE_SCOPE_NOTE_DRAFT_ID,
    }),
    [user?.lassoUserId, user?.organizationId],
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const { isLoading } = useEstimateDetail(estimation?.estimateId!);

  // Centralized upload functionality for sharing between components
  const {
    reset,
    uploadedFiles,
    isUploading: isFileUploading,
    uploadProgress,
    pdfValidationState,
    removeUploadedFile,
    uploadMultipleFiles,
  } = useUploadEstimationFiles(estimation?.estimateId ?? "");

  const isUploading = isLoading || isFileUploading;

  // files are uploaded to an estimate in two steps: 1) upload to s3 bucket then 2) save file object to the
  // estimate, `useUploadEstimationFiles` is responsible for uploading new files to the s3 file and we rely
  // on this useEffect to add file objects to the estimate once they're uploaded to s3
  useEffect(() => {
    onUpdateUploadedData({ files: uploadedFiles });
  }, [uploadedFiles]);

  const [estimateUploadState, setUploadedData] = useState<EstimateUploadState>({});
  useEffect(() => {
    reset();
    if (estimation === undefined) {
      setUploadedData({
        link: undefined,
        files: [],
        scopeNoteText: readScopeNoteDraft(newEstimateDraftParams) ?? "",
        tpaId: undefined,
        carrierId: undefined,
      });
    } else {
      // if this is an update to existing estimate, we'll set the estimate metadata that's shown in the upload screen
      const { ...data } = estimation;
      const restoredScopeNoteText = readScopeNoteDraft(newEstimateDraftParams);
      setUploadedData(restoredScopeNoteText === undefined ? data : { ...data, scopeNoteText: restoredScopeNoteText });
    }
  }, [estimation?.estimateId, newEstimateDraftParams, reset, show]);

  const onUpdateUploadedData = (updates: Partial<EstimateUploadState>) => {
    if (typeof updates.scopeNoteText === "string") {
      writeScopeNoteDraft(newEstimateDraftParams, updates.scopeNoteText);
    }

    setUploadedData((_estimateUploadState) => ({
      ..._estimateUploadState,
      ...updates,
    }));
  };

  const onSubmit = async () => {
    if (isLoading) {
      return;
    }
    await _onSubmit(estimateUploadState);
  };

  const { isFeatureEnabled } = useFeatureFlags();
  const mitigationEnabled = isFeatureEnabled("MITIGATION_ENABLED");
  const xactimateEstimateImportEnabled = import.meta.env.VITE_XACTIMATE_ESTIMATE_IMPORT_ENABLED === "true";
  const contentsEnabled = isFeatureEnabled("CONTENTS_ENABLED");
  const xcelerateIdEnabled = isFeatureEnabled("XCELERATE_ID_ENABLED");
  const moldEnabled = isFeatureEnabled("MOLD_ENABLED");
  const fireMitigationEnabled = isFeatureEnabled("FIRE_MITIGATION_ENABLED");
  const biohazardEnabled = isFeatureEnabled("BIOHAZARD_ENABLED");
  const timeAndMaterialEnabled = isFeatureEnabled("TIME_AND_MATERIAL_ENABLED");

  const { data: rateCardsData, isLoading: isLoadingRateCards } = useGetRateCardsQuery();
  const rateCards = useMemo<RateCard[]>(() => (rateCardsData ?? []).filter((card) => !card.deleted), [rateCardsData]);

  useEffect(() => {
    const currentType = estimateUploadState.estimateType;
    if (currentType === EstimateType.TIME_AND_MATERIAL) {
      if (!estimateUploadState.rateCardId && rateCards.length === 1) {
        onUpdateUploadedData({ rateCardId: rateCards[0].id });
      }
    } else if (estimateUploadState.rateCardId) {
      onUpdateUploadedData({ rateCardId: undefined });
    }
  }, [estimateUploadState.estimateType, estimateUploadState.rateCardId, rateCards, onUpdateUploadedData]);

  const isMobile = useIsMobile();

  return (
    <div
      className={`absolute inset-0 flex flex-col bg-white pb-6 transition-all duration-300 ${!isMobile ? "px-4" : ""} ${
        show ? "pointer-events-auto z-20 opacity-100" : "pointer-events-none -z-20 opacity-0"
      }`}
    >
      {isMobile ? (
        <UploadScopeNotesMobileView
          estimation={estimation}
          onSubmit={onSubmit}
          estimateUploadState={estimateUploadState}
          onUpdateUploadedData={onUpdateUploadedData}
          onClose={onClose}
          xcelerateIdEnabled={xcelerateIdEnabled}
          onShowConfirmDialog={() => setShowConfirmDialog(true)}
          mitigationEnabled={mitigationEnabled}
          contentsEnabled={contentsEnabled}
          moldEnabled={moldEnabled}
          fireMitigationEnabled={fireMitigationEnabled}
          biohazardEnabled={biohazardEnabled}
          timeAndMaterialEnabled={timeAndMaterialEnabled}
          rateCards={rateCards}
          isLoadingRateCards={isLoadingRateCards}
          uploadedFiles={uploadedFiles}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          pdfValidationState={pdfValidationState}
          removeUploadedFile={removeUploadedFile}
          uploadMultipleFiles={uploadMultipleFiles}
        />
      ) : (
        <UploadScopeNotesDesktopView
          estimation={estimation}
          estimateUploadState={estimateUploadState}
          onSubmit={onSubmit}
          onClose={onClose}
          onUpdate={onUpdateUploadedData}
          xcelerateIdEnabled={xcelerateIdEnabled}
          onShowConfirmDialog={() => setShowConfirmDialog(true)}
          mitigationEnabled={mitigationEnabled}
          xactimateEstimateImportEnabled={xactimateEstimateImportEnabled}
          contentsEnabled={contentsEnabled}
          moldEnabled={moldEnabled}
          fireMitigationEnabled={fireMitigationEnabled}
          biohazardEnabled={biohazardEnabled}
          timeAndMaterialEnabled={timeAndMaterialEnabled}
          rateCards={rateCards}
          isLoadingRateCards={isLoadingRateCards}
          uploadedFiles={uploadedFiles}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          pdfValidationState={pdfValidationState}
          removeUploadedFile={removeUploadedFile}
          uploadMultipleFiles={uploadMultipleFiles}
          submitText={submitText}
        />
      )}

      <ConfirmDiscardDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={() => {
          clearScopeNoteDraft(newEstimateDraftParams);
          setShowConfirmDialog(false);
          onClose();
        }}
        estimateType={estimateUploadState?.estimateType}
        hadFiles={(uploadedFiles?.length ?? 0) > 0}
      />
    </div>
  );
};

const UploadScopeNotesDesktopView = ({
  estimation,
  estimateUploadState,
  onUpdate,
  onSubmit: _onSubmit,
  onShowConfirmDialog,
  mitigationEnabled,
  xactimateEstimateImportEnabled,
  contentsEnabled,
  moldEnabled,
  fireMitigationEnabled,
  biohazardEnabled,
  timeAndMaterialEnabled,
  rateCards,
  isLoadingRateCards,
  uploadedFiles,
  isUploading,
  uploadProgress,
  pdfValidationState,
  removeUploadedFile,
  uploadMultipleFiles,
  submitText,
}: {
  // Right now we only use `estimation` for the `UploadExternal3DScanLink` component; TODO: refactor that
  // componet to use `estimateUploadState`
  estimation: Estimation | undefined;
  estimateUploadState: EstimateUploadState;
  onUpdate: (updates: Partial<EstimateUploadState>) => void;
  onSubmit: () => void;
  onClose: () => void;
  xcelerateIdEnabled: boolean;
  onShowConfirmDialog: () => void;
  mitigationEnabled: boolean;
  xactimateEstimateImportEnabled: boolean;
  contentsEnabled: boolean;
  moldEnabled: boolean;
  fireMitigationEnabled: boolean;
  biohazardEnabled: boolean;
  timeAndMaterialEnabled: boolean;
  rateCards: RateCard[];
  isLoadingRateCards: boolean;
  uploadedFiles: EstimateFileType[];
  isUploading: boolean;
  uploadProgress: UploadProgress;
  pdfValidationState: PdfValidationState;
  removeUploadedFile: (fileId: string) => void;
  uploadMultipleFiles: (
    files: File[],
    fileType: EstimateFile,
    postUploadCommand: string,
    tpa?: string,
    insuranceCarrier?: string,
  ) => Promise<void>;
  submitText: string;
}) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();
  const isScopeNoteUpload = isFeatureEnabled("SCOPE_NOTE_UPLOAD_CONFIG");
  const showExternal3DScanUpload = isFeatureEnabled("SHOW_DOCUSKETCH_UPLOAD");

  const [is3DLinkValidating, setIs3DLinkValidating] = useState(false);
  const { hasValidationInProgress, hasUnknownFormat, hasMixedKnownFormats } = useMemo(
    () => getPdfValidationSummary(uploadedFiles, pdfValidationState),
    [uploadedFiles, pdfValidationState],
  );
  const uploadValidationWarningMessage = useMemo(() => {
    if (hasUnknownFormat) {
      return "Remove documents with unsupported formats to generate line items.";
    }
    if (hasMixedKnownFormats) {
      return "All documents must have the same format.";
    }
    return "";
  }, [hasUnknownFormat, hasMixedKnownFormats]);

  const isNextButtonDisabled = useMemo(() => {
    const hasExternal3DScanLink = !!estimateUploadState?.docusketchId || !!estimateUploadState?.matterportId;
    const hasScopeNotes = !!estimateUploadState.scopeNoteText;

    const isRecon = estimateUploadState?.estimateType === EstimateType.RECONSTRUCTION;
    const isMit = estimateUploadState?.estimateType === EstimateType.MITIGATION;
    const isContents = estimateUploadState?.estimateType === EstimateType.CONTENTS;
    const isMold = estimateUploadState?.estimateType === EstimateType.MOLD;
    const isFireMit = estimateUploadState?.estimateType === EstimateType.FIRE_MITIGATION;
    const isBiohazard = estimateUploadState?.estimateType === EstimateType.BIOHAZARD;
    const isTimeAndMaterial = estimateUploadState?.estimateType === EstimateType.TIME_AND_MATERIAL;

    const someFilesAreMit = (estimateUploadState?.files || []).some((file) => file.fileType === EstimateFile.MITIGATION_REPORT);
    const someFilesAreContents = (estimateUploadState?.files || []).some((file) => file.fileType === EstimateFile.CONTENTS_TEMPLATE);
    // A carrier's Xactimate estimate is enough on its own to build from, whatever the estimate's type
    const someFilesAreXactimateEstimates = (estimateUploadState?.files || []).some(
      (file) => file.fileType === EstimateFile.XACTIMATE_ESTIMATE,
    );

    if (isUploading) {
      return true;
    }

    if (is3DLinkValidating) {
      return true;
    }

    if (hasValidationInProgress) {
      return true;
    }

    if (hasUnknownFormat) {
      return true;
    }

    if (hasMixedKnownFormats) {
      return true;
    }

    // A rate card gates a T&M estimate before anything else can enable it
    if (isTimeAndMaterial && !estimateUploadState?.rateCardId) {
      return true;
    }

    // A carrier's Xactimate estimate is enough on its own to build from, whatever the estimate's type
    if (someFilesAreXactimateEstimates && estimateUploadState?.estimateType) {
      return false;
    }

    if (isRecon) {
      return !hasExternal3DScanLink && !hasScopeNotes;
    }
    if (isContents) {
      return !someFilesAreContents && !hasScopeNotes;
    }
    if (isMit || isMold || isFireMit) {
      return !someFilesAreMit && !hasScopeNotes;
    }

    if (isBiohazard || isTimeAndMaterial) {
      return !hasScopeNotes;
    }

    return true;
  }, [estimateUploadState, is3DLinkValidating, hasValidationInProgress, hasUnknownFormat, hasMixedKnownFormats, isUploading]);

  const showRateCardPicker = estimateUploadState?.estimateType === EstimateType.TIME_AND_MATERIAL;
  const showRateCardError = showRateCardPicker && !estimateUploadState?.rateCardId && !isLoadingRateCards;

  return (
    <div className="flex h-full flex-col">
      <div className="-mx-4 mb-2">
        <RebuildHeader rightSection={<X className="h-6 w-6 cursor-pointer" onClick={() => onShowConfirmDialog()} />} />
      </div>
      <div className="flex-1 overflow-y-auto">
        {!isScopeNoteUpload && (
          <UploadEstimationDataSection>
            <UploadEstimationFiles
              estimation={estimation}
              onUpdate={onUpdate}
              isScopeNoteUpload={isScopeNoteUpload}
              uploadedFiles={uploadedFiles}
              uploadProgress={uploadProgress}
              pdfValidationState={pdfValidationState}
              uploadValidationWarningMessage={uploadValidationWarningMessage}
              removeUploadedFile={removeUploadedFile}
              showExternal3DScanUpload={showExternal3DScanUpload}
            />
          </UploadEstimationDataSection>
        )}
        {isScopeNoteUpload && (
          <UploadEstimationDataSection>
            <div>
              <div className="mb-6 flex w-full flex-col items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {(mitigationEnabled || contentsEnabled || moldEnabled || fireMitigationEnabled) && (
                    <EstimateTypeToggle
                      mitigationEnabled={mitigationEnabled}
                      contentsEnabled={contentsEnabled}
                      moldEnabled={moldEnabled}
                      fireMitigationEnabled={fireMitigationEnabled}
                      biohazardEnabled={biohazardEnabled}
                      timeAndMaterialEnabled={timeAndMaterialEnabled}
                      rateCardsCount={rateCards.length}
                      estimateType={estimateUploadState.estimateType}
                      noteType={estimateUploadState.noteType}
                      estimateId={estimateUploadState.estimateId}
                      onUpdate={onUpdate}
                    />
                  )}
                </div>
                {showRateCardPicker && (
                  <RateCardSelect
                    rateCards={rateCards}
                    value={estimateUploadState.rateCardId}
                    onChange={(value) => onUpdate({ rateCardId: value })}
                    isLoading={isLoadingRateCards}
                    hasError={showRateCardError}
                  />
                )}
                <div className="flex shrink-0 gap-2">
                  <TPASelect value={estimateUploadState.tpaId} onChange={(value) => onUpdate({ tpaId: value })} />
                  <CarrierSelect value={estimateUploadState.carrierId} onChange={(value) => onUpdate({ carrierId: value })} />
                </div>
              </div>
              <div className="mb-4 flex h-full flex-row justify-between gap-2">
                {/* Left 65% - Scope Notes */}
                <div className={`w-full`}>
                  <UploadScopeNotes estimateUploadState={estimateUploadState} onUpdate={onUpdate} isScopeNoteUpload={isScopeNoteUpload} />
                </div>
                {/* Right 35% - Mitigation Details, 3D Scan Link and Photo Upload */}
                <div className="flex flex-[0_0_35%] flex-col gap-4">
                  {showExternal3DScanUpload && (
                    <UploadExternal3DScanLink
                      estimation={estimation}
                      onUpdate={onUpdate}
                      isScopeNoteUpload={isScopeNoteUpload}
                      onIsValidatingChange={setIs3DLinkValidating}
                    />
                  )}
                  {mitigationEnabled &&
                    (estimateUploadState?.estimateType === EstimateType.MITIGATION ||
                      estimateUploadState?.estimateType === EstimateType.MOLD) && (
                      <EstimateFileUploadSection
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        pdfValidationState={pdfValidationState}
                        uploadValidationWarningMessage={uploadValidationWarningMessage}
                        removeUploadedFile={removeUploadedFile}
                        uploadMultipleFiles={uploadMultipleFiles}
                        tpa={estimateUploadState.tpa ?? ""}
                        insuranceCarrier={estimateUploadState.insuranceCarrier ?? ""}
                        fileType={EstimateFile.MITIGATION_REPORT}
                      />
                    )}
                  {contentsEnabled && estimateUploadState?.estimateType === EstimateType.CONTENTS && (
                    <EstimateFileUploadSection
                      uploadedFiles={uploadedFiles}
                      uploadProgress={uploadProgress}
                      pdfValidationState={pdfValidationState}
                      uploadValidationWarningMessage={uploadValidationWarningMessage}
                      removeUploadedFile={removeUploadedFile}
                      uploadMultipleFiles={uploadMultipleFiles}
                      tpa={estimateUploadState.tpa ?? ""}
                      insuranceCarrier={estimateUploadState.insuranceCarrier ?? ""}
                      fileType={EstimateFile.CONTENTS_TEMPLATE}
                    />
                  )}
                  {xactimateEstimateImportEnabled && (
                    <EstimateFileUploadSection
                      uploadedFiles={uploadedFiles}
                      uploadProgress={uploadProgress}
                      pdfValidationState={pdfValidationState}
                      uploadValidationWarningMessage={uploadValidationWarningMessage}
                      removeUploadedFile={removeUploadedFile}
                      uploadMultipleFiles={uploadMultipleFiles}
                      tpa={estimateUploadState.tpa ?? ""}
                      insuranceCarrier={estimateUploadState.insuranceCarrier ?? ""}
                      fileType={EstimateFile.XACTIMATE_ESTIMATE}
                    />
                  )}
                </div>
              </div>
              <UploadedFileList
                files={uploadedFiles.filter(
                  (file) =>
                    file.fileType !== EstimateFile.MITIGATION_REPORT &&
                    file.fileType !== EstimateFile.CONTENTS_TEMPLATE &&
                    file.fileType !== EstimateFile.XACTIMATE_ESTIMATE,
                )}
                uploadProgress={uploadProgress}
                pdfValidationState={pdfValidationState}
                removeUploadedFile={removeUploadedFile}
                excludeFileTypes={[
                  ...(mitigationEnabled &&
                  (estimateUploadState?.estimateType === EstimateType.MITIGATION || estimateUploadState?.estimateType === EstimateType.MOLD)
                    ? [EstimateFile.MITIGATION_REPORT]
                    : []),
                  EstimateFile.XACTIMATE_ESTIMATE,
                ]}
              />
            </div>
          </UploadEstimationDataSection>
        )}
      </div>

      <div className={`mt-2 flex w-full flex-shrink-0 flex-row justify-end gap-2`}>
        <Button
          data-testid="cancel-estimate"
          variant="outline"
          size="lg"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.ESTIMATE_CANCEL_INITIATED, {
              estimate_type: estimateUploadState?.estimateType,
              had_files: (uploadedFiles?.length ?? 0) > 0,
            });
            onShowConfirmDialog();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="accent"
          data-testid="submit-estimate"
          onClick={() => {
            trackEvent(ANALYTICS_EVENTS.ESTIMATE_SUBMITTED, {
              estimate_type: estimateUploadState?.estimateType ?? EstimateType.RECONSTRUCTION,
              file_count: uploadedFiles?.length ?? 0,
              has_photos: uploadedFiles?.some((f) => f.fileType === EstimateFile.IMAGE) ?? false,
              has_3d_scan: !!(estimateUploadState?.docusketchId || estimateUploadState?.matterportId),
            });
            _onSubmit();
          }}
          disabled={isNextButtonDisabled}
          size="lg"
          className={isUploading ? "animate-pulse" : ""}
        >
          {submitText}
        </Button>
      </div>
    </div>
  );
};

const UploadEstimationDataSection = ({ children }: { children: ReactNode }) => (
  <div className="mt-2 mb-2 overflow-scroll rounded-lg bg-gray-100 p-4">{children}</div>
);

const UploadEstimationFiles = ({
  estimation,
  onUpdate,
  uploadedFiles,
  uploadProgress,
  pdfValidationState,
  uploadValidationWarningMessage,
  removeUploadedFile,
  showExternal3DScanUpload,
}: {
  estimation: Estimation | undefined;
  onUpdate: (updates: Partial<Estimation>) => void;
  isScopeNoteUpload?: boolean;
  uploadedFiles: any[];
  uploadProgress: any;
  pdfValidationState: PdfValidationState;
  uploadValidationWarningMessage?: string;
  removeUploadedFile: (fileId: string) => void;
  showExternal3DScanUpload?: boolean;
}) => {
  const external3DScanComponent = <UploadExternal3DScanLink estimation={estimation} onUpdate={onUpdate} />;

  return (
    <div>
      <div className="flex flex-row justify-center gap-40">{showExternal3DScanUpload && external3DScanComponent}</div>
      <UploadedFileList
        files={uploadedFiles}
        uploadProgress={uploadProgress}
        pdfValidationState={pdfValidationState}
        removeUploadedFile={removeUploadedFile}
      />
      {!!uploadValidationWarningMessage && <p className="mt-2 text-sm text-amber-700">{uploadValidationWarningMessage}</p>}
    </div>
  );
};

type UploadState =
  | "EMPTY"
  | "WRONG_FORMAT"
  | "VALIDATING"
  | "ERROR_ON_VALIDATION"
  | "MIGHT_BE_PRIVATE_LINK"
  | "LINK_DOESNT_WORK"
  | "VALIDATION_PASSED";

const UPLOAD_STATE_TO_HELPER_MESSAGE: Record<UploadState, { color: string; message: string }> = {
  ["EMPTY"]: {
    color: "",
    message: "",
  },
  ["WRONG_FORMAT"]: {
    color: "text-red-500",
    message: `It looks like the link you uploaded is formatted incorrectly - please verify that your link is correct`,
  },
  ["VALIDATING"]: {
    color: "text-blue-500",
    message: `Validating link...`,
  },
  ["VALIDATION_PASSED"]: {
    color: "text-green-500",
    message: `Validated 3D scan link`,
  },
  ["MIGHT_BE_PRIVATE_LINK"]: {
    color: "text-red-500",
    message: `the link you shared is not readable - please share the public link`,
  },
  ["ERROR_ON_VALIDATION"]: {
    color: "text-red-500",
    message: `Something went wrong when attempting to validate this link, please try again`,
  },
  ["LINK_DOESNT_WORK"]: {
    color: "text-red-500",
    message: `This link doesn't work, please try another one.`,
  },
};

const UploadExternal3DScanLink = ({
  estimation,
  onUpdate,
  onIsValidatingChange,
  isScopeNoteUpload,
}: {
  estimation: Estimation | undefined;
  onUpdate: (updates: Partial<Estimation>) => void;
  onIsValidatingChange?: (isValidating: boolean) => void;
  isScopeNoteUpload?: boolean;
}) => {
  const [uploadState, setUploadState] = useState<UploadState>("EMPTY");

  const [external3DScanLink, setExternal3DScanLink] = useState("");
  const external3DScanLinkDebounced = useDebounceState(external3DScanLink, 250);

  const existingLinks = useMemo(() => estimation?.links || [], [estimation?.links]);

  useEffect(() => {
    let existingExternal3DScanLink: string | undefined;
    if (estimation?.docusketchId) {
      existingExternal3DScanLink = existingLinks.find((link) => link.includes("app.docusketch.com"));
    }
    if (estimation?.matterportId) {
      existingExternal3DScanLink = existingLinks.find((link) => link.includes("my.matterport.com"));
    }
    if (existingExternal3DScanLink) {
      setExternal3DScanLink(existingExternal3DScanLink);
      setUploadState("VALIDATION_PASSED");
    } else {
      setUploadState("EMPTY");
      setExternal3DScanLink("");
    }
  }, [estimation?.estimateId, existingLinks]);

  useEffect(() => {
    const onNewLink = async (link: string) => {
      if (!link) {
        setUploadState("EMPTY");
        const updatedLinks = existingLinks.filter((link) => !link.includes("app.docusketch.com") || !link.includes("my.matterport.com"));
        onUpdate({
          matterportId: undefined,
          docusketchId: undefined,
          ...(updatedLinks.length !== existingLinks.length ? { links: updatedLinks } : {}),
        });
        return;
      } else if (existingLinks.includes(link)) {
        setUploadState("VALIDATION_PASSED");
        return;
      }
      setUploadState("VALIDATING");

      const matterportId = link.match(/https:\/\/my\.matterport\.com\/show\/\?m=(.+)/)?.[1];

      if (matterportId) {
        const filteredLinks = existingLinks.filter((link) => !link.includes("my.matterport.com"));
        const updatedLinks = [...filteredLinks, link];
        onUpdate({ matterportId, links: updatedLinks });
        setUploadState("VALIDATION_PASSED");
        return;
      }

      const docusketchId = link.match(/https:\/\/app\.docusketch\.com\/player\/([^/?#]+)/)?.[1];

      if (!docusketchId) {
        setUploadState("WRONG_FORMAT");
        return;
      }

      const { status } = await estimateProvider.validateDocusketch({
        docusketchId,
      });
      if (status === 200) {
        setUploadState("VALIDATION_PASSED");
        const filteredLinks = existingLinks.filter((link) => !link.includes("app.docusketch.com"));
        const updatedLinks = [...filteredLinks, link];
        onUpdate({
          docusketchId,
          links: updatedLinks,
        });
      } else if (status === 403) {
        setUploadState("MIGHT_BE_PRIVATE_LINK");
      } else if (status === 500 || status === 404) {
        setUploadState("LINK_DOESNT_WORK");
      } else {
        setUploadState("ERROR_ON_VALIDATION");
      }
    };

    onNewLink(external3DScanLinkDebounced);
  }, [external3DScanLinkDebounced, existingLinks]);

  useEffect(() => {
    if (uploadState === "VALIDATING") {
      onIsValidatingChange?.(true);
    } else {
      onIsValidatingChange?.(false);
    }
  }, [uploadState]);

  const helperMessage: { color: string; message: string } | undefined = useMemo(
    () => UPLOAD_STATE_TO_HELPER_MESSAGE[uploadState],
    [uploadState],
  );

  return (
    <div
      className={`rounded-xl border-2 border-dashed border-gray-300 bg-white py-4 text-center ${isScopeNoteUpload ? "w-full" : "h-[240px] w-[320px]"}`}
    >
      <div className="flex flex-col items-center gap-2">
        <Link size={!isScopeNoteUpload ? 56 : 40} className="text-gray-600" />
        <p className="text-[14px] font-medium text-gray-800">Upload 3D Scan Link</p>
        <div className="mx-3 mt-3 w-full">
          <input
            type="url"
            id="link"
            value={external3DScanLink}
            onChange={(e) => setExternal3DScanLink(e.target.value)}
            placeholder="https://example.com"
            className="w-[85%] rounded-lg border border-gray-300 px-4 py-2 text-[14px] transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <p className="mt-2 !text-xs text-gray-500">Add your 3D scan link here</p>
        {helperMessage && <p className={`${helperMessage.color} mt-1 px-3 text-center text-xs`}>{helperMessage.message}</p>}
      </div>
    </div>
  );
};

const UploadScopeNotes = ({
  estimateUploadState,
  onUpdate,
  isScopeNoteUpload,
}: {
  estimateUploadState: EstimateUploadState;
  onUpdate: (updates: Partial<EstimateUploadState>) => void;
  isScopeNoteUpload?: boolean;
}) => {
  const [scopeNoteText, setScopeNoteText] = useState("");
  useEffect(() => setScopeNoteText(estimateUploadState.scopeNoteText ?? ""), [estimateUploadState.scopeNoteText]);

  const _updateScopeNotes = (text: string) => {
    setScopeNoteText(text);
    onUpdate({ scopeNoteText: text });
  };

  // dynamically resize text input (by changing number of rows) based on window height
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  useEffect(() => {
    window.addEventListener("resize", () => setWindowHeight(window.innerHeight));
  }, []);
  const numRows = useMemo(() => {
    if (isScopeNoteUpload) {
      return Math.max(12, Math.round(0.03 * windowHeight - 10)); // Larger for Titan layout
    } else {
      return Math.max(6, Math.round(0.02 * windowHeight - 15));
    }
  }, [windowHeight, isScopeNoteUpload]);

  const { fontMedium, fontVerySmall } = useGetStyles();

  return (
    <div
      className={`rounded-xl border-2 border-dashed border-gray-300 bg-white px-2 py-4 text-center ${isScopeNoteUpload ? "h-full w-full" : "w-[480px]"}`}
    >
      <div className="flex flex-col items-center gap-2">
        <div className={`flex ${"flex-col items-center"}`}>
          <FileText size={40} className="text-gray-600" />
          <div className="flex items-center justify-center">
            <p className={`${fontMedium} text-gray-800`}>Scope Notes</p>
          </div>
        </div>

        <div className={`mx-3 mt-3 w-full`}>
          <textarea
            onChange={(e) => _updateScopeNotes(e.target.value)}
            value={scopeNoteText}
            id="notes"
            rows={numRows}
            placeholder="Enter scope notes here..."
            className={`${isScopeNoteUpload ? "w-[95%]" : "w-[90%]"} resize-vertical rounded-lg border border-gray-300 px-4 py-2 text-[14px] transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500`}
          />
        </div>
        <p className={`${fontVerySmall} mt-2 text-gray-500`}>Add room-specific notes and details</p>
      </div>
    </div>
  );
};

const EstimateTypeToggle = ({
  mitigationEnabled,
  contentsEnabled,
  moldEnabled,
  fireMitigationEnabled,
  biohazardEnabled,
  timeAndMaterialEnabled,
  rateCardsCount,
  estimateId,
  estimateType,
  noteType,
  onUpdate,
}: {
  mitigationEnabled: boolean;
  contentsEnabled: boolean;
  moldEnabled: boolean;
  fireMitigationEnabled: boolean;
  biohazardEnabled: boolean;
  timeAndMaterialEnabled: boolean;
  rateCardsCount: number;
  estimateId: string | undefined;
  estimateType: EstimateType | undefined;
  noteType: NoteType | undefined;
  onUpdate: (updates: Partial<Estimation>) => void;
}) => {
  const isTimeAndMaterialDisabled = rateCardsCount === 0;
  const selectedType = estimateType;

  // Set default to MITIGATION when component mounts if no type is set
  useEffect(() => {
    if (!estimateType) {
      onUpdate({ estimateType: EstimateType.MITIGATION, noteType: getNoteTypeFromEstimateType(EstimateType.MITIGATION) });
    }
  }, [estimateId, estimateType, onUpdate]);

  const handleTypeChange = (type: EstimateType) => {
    if (type === estimateType) return;
    onUpdate({ estimateType: type, noteType: getNoteTypeFromEstimateType(type) });
  };

  const handleReconstructionNoteTypeChange = (newNoteType: NoteType) => {
    if (newNoteType === noteType) return;
    onUpdate({ noteType: newNoteType });
  };

  const isMitigationSelected = selectedType === EstimateType.MITIGATION;
  const isReconstructionSelected = selectedType === EstimateType.RECONSTRUCTION;
  const isContentsSelected = selectedType === EstimateType.CONTENTS;
  const isMoldSelected = selectedType === EstimateType.MOLD;
  const isFireMitigationSelected = selectedType === EstimateType.FIRE_MITIGATION;
  const isBiohazardSelected = selectedType === EstimateType.BIOHAZARD;
  const isTimeAndMaterialSelected = selectedType === EstimateType.TIME_AND_MATERIAL;

  return (
    <div className="min-w-0">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-700">Choose the type of estimate you want to create</p>
        <div className="inline-flex gap-1">
          {mitigationEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.MITIGATION)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isMitigationSelected ? "rounded-lg !bg-black !text-white" : "rounded-l-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Mitigation
            </Button>
          )}
          <Button
            variant="accent"
            size="lg"
            onClick={() => handleTypeChange(EstimateType.RECONSTRUCTION)}
            className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
              isReconstructionSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Reconstruction
          </Button>
          {contentsEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.CONTENTS)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isContentsSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Contents
            </Button>
          )}
          {moldEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.MOLD)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isMoldSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Mold
            </Button>
          )}
          {fireMitigationEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.FIRE_MITIGATION)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isFireMitigationSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Fire Mitigation
            </Button>
          )}
          {biohazardEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.BIOHAZARD)}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isBiohazardSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Biohazard
            </Button>
          )}
          {timeAndMaterialEnabled && (
            <Button
              variant="accent"
              size="lg"
              onClick={() => handleTypeChange(EstimateType.TIME_AND_MATERIAL)}
              disabled={isTimeAndMaterialDisabled}
              title={isTimeAndMaterialDisabled ? "No rate cards available — create one in Tools" : undefined}
              className={`px-6 py-3 text-sm font-medium transition-all duration-300 ${
                isTimeAndMaterialSelected ? "rounded-lg !bg-black !text-white" : "rounded-r-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
              } ${isTimeAndMaterialDisabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              Time and Material
            </Button>
          )}
        </div>
        {isReconstructionSelected && (
          <div
            role="radiogroup"
            aria-label="Reconstruction type"
            className="inline-flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1"
          >
            {(
              [
                { value: NoteType.INTERIOR_RECONSTRUCTION, label: "Interior" },
                { value: NoteType.EXTERIOR_RECONSTRUCTION, label: "Exterior" },
              ] as const
            ).map(({ value, label }) => {
              const isActive = noteType === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => handleReconstructionNoteTypeChange(value)}
                  className={`cursor-pointer rounded-md px-4 py-1.5 text-sm font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-black focus-visible:outline-none ${
                    isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const UploadScopeNotesMobileView = ({
  estimation,
  estimateUploadState,
  onUpdateUploadedData,
  onSubmit: _onSubmit,
  xcelerateIdEnabled,
  onShowConfirmDialog,
  mitigationEnabled,
  contentsEnabled,
  moldEnabled,
  fireMitigationEnabled,
  biohazardEnabled,
  timeAndMaterialEnabled,
  rateCards,
  isLoadingRateCards,
  uploadedFiles,
  uploadProgress,
  pdfValidationState,
  removeUploadedFile,
  uploadMultipleFiles,
}: {
  // right now the only reason why we pass in `estimation` and `estimateUploadState` is because <Conversation ...>
  // requires the `estimation` object; we should refactor that component to use `estimateUploadState` at some point
  estimation: Estimation | undefined;
  estimateUploadState: EstimateUploadState;
  onUpdateUploadedData: (updates: Partial<EstimateUploadState>) => void;
  onSubmit: () => void;
  onClose: () => void;
  xcelerateIdEnabled: boolean;
  onShowConfirmDialog: () => void;
  mitigationEnabled: boolean;
  contentsEnabled: boolean;
  moldEnabled: boolean;
  fireMitigationEnabled: boolean;
  biohazardEnabled: boolean;
  timeAndMaterialEnabled: boolean;
  rateCards: RateCard[];
  isLoadingRateCards: boolean;
  uploadedFiles: EstimateFileType[];
  isUploading: boolean;
  uploadProgress: UploadProgress;
  pdfValidationState: PdfValidationState;
  removeUploadedFile: (fileId: string) => void;
  uploadMultipleFiles: (
    files: File[],
    fileType: EstimateFile,
    postUploadCommand: string,
    tpa?: string,
    insuranceCarrier?: string,
  ) => Promise<void>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  const organizationId = useSelector(appSelector.organizationId);

  const isExcelUploadEnabled = mobileExcelUploadForOrganizations.includes(organizationId);
  const isMobileAddressUploadEnabled = mobileAddressImportForOrganizations.includes(organizationId);

  const conversation = useConversation({
    onConnect: () => console.log("Connected"),
    onDisconnect: () => console.log("Disconnected"),
    onMessage: () => console.log("Message"),
    onError: (error) => console.error("Error:", error),
  });

  const handleEndConversationAndShowDialog = async () => {
    trackEvent(ANALYTICS_EVENTS.ESTIMATE_CANCEL_INITIATED, {
      estimate_type: estimateUploadState?.estimateType,
      had_files: (uploadedFiles?.length ?? 0) > 0,
    });
    onShowConfirmDialog();
    // End conversation if it's in progress
    if (["connected", "connecting"].includes(conversation.status)) {
      await conversation.endSession();
    }
  };

  const [projectName, setProjectName] = useState<string>(estimateUploadState?.projectName ?? "");
  const [propertyAddress, setPropertyAddress] = useState<string>(estimateUploadState?.propertyAddress ?? "");
  const [scopeNoteText, setScopeNoteText] = useState<string>("");
  const [xcelerateId, setXcelerateId] = useState<string>("");
  const [projectNameHasXcelerateId, setProjectNameHasXcelerateId] = useState<boolean>(false);
  const [isLoadingConversationData, setIsLoadingConversationData] = useState<boolean>(false);

  useEffect(() => {
    setScopeNoteText(estimateUploadState.scopeNoteText ?? "");
  }, [estimateUploadState.scopeNoteText]);

  // Note type is mostly the same as Estimate type, except for Reconstruction.  Since we need to invoke different system
  // prompts for interior and exterior reconstruction (for right now), we need to track this separately.
  const [noteType, setNoteType] = useState<NoteType>(getNoteTypeFromEstimateType(estimateUploadState?.estimateType));

  // Auto-resize textarea when scopeNotes changes (for autofill scenarios)
  useEffect(() => {
    const textarea = document.getElementById("scopeNotes") as HTMLTextAreaElement;
    if (textarea) {
      setTimeout(() => {
        textarea.style.height = "auto";
        const newHeight = Math.min(textarea.scrollHeight, 400);
        textarea.style.height = `${newHeight}px`;
      }, 0);
    }
  }, [scopeNoteText]);

  useEffect(() => {
    setProjectName(estimateUploadState?.projectName ?? "");
    setPropertyAddress(estimateUploadState?.propertyAddress ?? "");
    setScopeNoteText("");
    setIsLoading(false);
    setXcelerateId(xcelerateIdEnabled ? (estimateUploadState?.estimateReferenceNumber ?? "") : "");
    setProjectNameHasXcelerateId(false);
    setNoteType(getNoteTypeFromEstimateType(estimateUploadState?.estimateType));
  }, [estimateUploadState?.estimateId]);

  const [isWorkAfterHoursAutoDetected, setIsWorkAfterHoursAutoDetected] = useState(false);

  const _updateScopeNotes = (updates: Partial<AddNoteBody>) => {
    if (typeof updates.text === "string") {
      setScopeNoteText(updates.text);
    }

    if (updates.workAfterHours === false && isWorkAfterHoursAutoDetected) {
      setIsWorkAfterHoursAutoDetected(false);
    }

    onUpdateUploadedData({
      ...(typeof updates.text === "string" ? { scopeNoteText: updates.text } : {}),
      ...(typeof updates.workAfterHours === "boolean" ? { scopeNoteWorkAfterHours: updates.workAfterHours } : {}),
    });
  };

  const _updateProjectName = useCallback(() => {
    onUpdateUploadedData({ projectName });
  }, [projectName, onUpdateUploadedData]);

  const _updatePropertyAddress = useCallback(
    (address: string) => {
      setPropertyAddress(address);
      onUpdateUploadedData({ propertyAddress: address });
    },
    [onUpdateUploadedData],
  );

  const _onAddressComplete = useCallback(
    (address: string, addressComponents?: google.maps.GeocoderAddressComponent[]) => {
      setPropertyAddress(address);
      onUpdateUploadedData({
        propertyAddress: address,
        propertyAddressComponents: JSON.stringify(addressComponents),
      });
    },
    [onUpdateUploadedData],
  );

  const _updateXcelerateId = useCallback(() => {
    onUpdateUploadedData({ estimateReferenceNumber: xcelerateId });

    let finalProjectName;
    if (xcelerateId) {
      if (projectNameHasXcelerateId) {
        // Replace existing Xcelerate ID with new one
        const nameWithoutPrefix = projectName.split(": ").slice(1).join(": ");
        finalProjectName = `${xcelerateId}: ${nameWithoutPrefix}`;
      } else {
        // Add Xcelerate ID as prefix
        finalProjectName = `${xcelerateId}: ${projectName}`;
        setProjectNameHasXcelerateId(true);
      }
    } else {
      // If no Xcelerate ID, remove prefix if it exists
      if (projectNameHasXcelerateId) {
        finalProjectName = projectName.split(": ").slice(1).join(": ");
        setProjectNameHasXcelerateId(false);
      } else {
        finalProjectName = projectName;
      }
    }

    setProjectName(finalProjectName);
    onUpdateUploadedData({ projectName: finalProjectName });
  }, [xcelerateId, projectName, projectNameHasXcelerateId, onUpdateUploadedData, setProjectName, isWorkAfterHoursAutoDetected]);

  const [showThatRequiredAreFields, setShowThatRequiredAreFields] = useState(false);
  useEffect(() => setShowThatRequiredAreFields(false), [estimateUploadState?.estimateId]);

  const onSubmitButtonClicked = () => {
    if (isLoading) {
      return;
    }

    if (isSubmitButtonDisabled) {
      setShowThatRequiredAreFields(true);
      return;
    }

    trackEvent(ANALYTICS_EVENTS.ESTIMATE_SUBMITTED, {
      estimate_type: estimateUploadState?.estimateType ?? EstimateType.RECONSTRUCTION,
      file_count: uploadedFiles?.length ?? 0,
      has_photos: uploadedFiles?.some((f) => f.fileType === EstimateFile.IMAGE) ?? false,
      has_3d_scan: !!(estimateUploadState?.docusketchId || estimateUploadState?.matterportId),
    });

    setIsLoading(true);
    _onSubmit();
  };

  const [showMissingProjectName, setShowMissingProjectName] = useState(false);
  useEffect(() => {
    setShowMissingProjectName(showThatRequiredAreFields && !projectName.trim());
  }, [showThatRequiredAreFields, projectName]);

  const [showMissingNotesAndFiles, setShowMissingNotesAndFiles] = useState(false);
  useEffect(() => {
    setShowMissingNotesAndFiles(showThatRequiredAreFields && !scopeNoteText.trim() && !uploadedFiles.length);
  }, [showThatRequiredAreFields, scopeNoteText, uploadedFiles]);
  const { hasValidationInProgress, hasUnknownFormat, hasMixedKnownFormats } = useMemo(
    () => getPdfValidationSummary(uploadedFiles, pdfValidationState),
    [uploadedFiles, pdfValidationState],
  );
  const uploadValidationWarningMessage = useMemo(() => {
    if (hasUnknownFormat) {
      return "Remove documents with unsupported formats to generate line items.";
    }
    if (hasMixedKnownFormats) {
      return "All documents must have the same format.";
    }
    return "";
  }, [hasUnknownFormat, hasMixedKnownFormats]);

  const isMissingRateCard = estimateUploadState?.estimateType === EstimateType.TIME_AND_MATERIAL && !estimateUploadState?.rateCardId;
  const isSubmitButtonDisabled = useMemo(
    () =>
      projectName === "" ||
      (scopeNoteText === "" && uploadedFiles.length === 0) ||
      hasValidationInProgress ||
      hasUnknownFormat ||
      hasMixedKnownFormats ||
      isMissingRateCard ||
      ["connected", "connecting"].includes(conversation.status), // disable while conversation in progress
    [
      projectName,
      scopeNoteText,
      conversation.status,
      uploadedFiles,
      hasValidationInProgress,
      hasUnknownFormat,
      hasMixedKnownFormats,
      isMissingRateCard,
    ],
  );

  const missingFieldsText = useMemo(() => {
    if (!showMissingNotesAndFiles && !showMissingProjectName) {
      return;
    }

    let _missingFieldsText = "Must ";
    if (showMissingProjectName) {
      _missingFieldsText += `add a project name`;
      if (showMissingNotesAndFiles) {
        _missingFieldsText += ` and `;
      }
    }
    if (showMissingNotesAndFiles) {
      _missingFieldsText += isExcelUploadEnabled ? `either upload a file or add scope notes` : "add scope notes";
    }
    _missingFieldsText += "!";

    return _missingFieldsText;
  }, [showMissingNotesAndFiles, showMissingProjectName]);

  const missingFieldBorderTailwind = "bg-red-50 border-1 border-red-500 focus:ring-red-500";
  const missingFieldTextTailwind = `text-red-600 text-sm`;

  return (
    <div className="flex h-[100vh] w-full flex-col overflow-hidden bg-gray-50">
      <header className="z-10 w-full flex-shrink-0 bg-white p-4 text-black transition-all duration-300 ease-in-out">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center space-x-2">
            <img src="/logo-black.svg" alt="Rebuild" className="h-5" />
          </div>
          <div className="flex items-center space-x-2">
            <EstimateTypeDropdown
              mitigationEnabled={mitigationEnabled}
              contentsEnabled={contentsEnabled}
              moldEnabled={moldEnabled}
              fireMitigationEnabled={fireMitigationEnabled}
              biohazardEnabled={biohazardEnabled}
              timeAndMaterialEnabled={timeAndMaterialEnabled}
              rateCards={rateCards}
              estimateType={estimateUploadState.estimateType}
              estimateId={estimateUploadState.estimateId!}
              onUpdate={onUpdateUploadedData}
              noteType={noteType}
              setNoteType={setNoteType}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="min-h-[calc(100vh-200px)]">
          <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
            <label htmlFor="projectName" className="mb-3 block text-lg font-medium text-gray-800">
              Project Name:
            </label>
            <input
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={() => _updateProjectName()}
              placeholder="Enter project name..."
              className={`w-full rounded-lg p-3 text-gray-700 transition-colors focus:ring-2 focus:outline-none ${
                showMissingProjectName ? missingFieldBorderTailwind : "bg-gray-100 focus:ring-blue-500"
              }`}
            />
            {showMissingProjectName && <p className={`mt-1 ${missingFieldTextTailwind}`}>Project name is required</p>}
          </div>

          {isMobileAddressUploadEnabled && (
            <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
              <label htmlFor="propertyAddress" className="mb-3 block text-lg font-medium text-gray-800">
                Address:
              </label>
              <AddressAutoComplete
                value={propertyAddress}
                onChange={_updatePropertyAddress}
                onComplete={_onAddressComplete}
                placeholder="Enter property address..."
                className="w-full rounded-lg bg-gray-100 p-3 text-gray-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          )}

          {xcelerateIdEnabled && (
            <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
              <label htmlFor="xcelerateId" className="mb-3 block text-lg font-medium text-gray-800">
                Xcelerate ID:
              </label>
              <input
                id="xcelerateId"
                type="text"
                value={xcelerateId}
                onChange={(e) => setXcelerateId(e.target.value)}
                onBlur={() => _updateXcelerateId()}
                placeholder="Enter Xcelerate ID..."
                className={`w-full rounded-lg bg-gray-100 p-3 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none`}
              />
            </div>
          )}

          {estimateUploadState?.estimateType === EstimateType.TIME_AND_MATERIAL && (
            <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
              <RateCardSelect
                rateCards={rateCards}
                value={estimateUploadState.rateCardId}
                onChange={(value) => onUpdateUploadedData({ rateCardId: value })}
                isLoading={isLoadingRateCards}
                hasError={isMissingRateCard && showThatRequiredAreFields}
              />
            </div>
          )}

          <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
            <div className="flex h-full flex-col items-center justify-center">
              <Conversation
                estimateId={estimateUploadState?.estimateId || ""}
                conversation={conversation}
                onScopeNotesReceived={(notes) => {
                  _updateScopeNotes({ text: notes });
                }}
                onProjectNameReceived={(name) => {
                  let newProjectName;
                  if (projectNameHasXcelerateId && xcelerateId) {
                    // If we already have an Xcelerate ID prefix, append the new name after it
                    newProjectName = `${xcelerateId}: ${name}`;
                  } else {
                    // Otherwise use existing logic
                    newProjectName = projectName ? projectName : name;
                  }
                  setProjectName(newProjectName);
                  onUpdateUploadedData({ projectName: newProjectName });
                }}
                onLoadingStateChange={setIsLoadingConversationData}
                onUpdate={onUpdateUploadedData}
                estimation={estimation}
                estimateType={estimateUploadState?.estimateType ?? EstimateType.MITIGATION}
                noteType={noteType}
              />
            </div>
          </div>

          {/* File Upload Section */}
          {isExcelUploadEnabled &&
            (estimateUploadState?.estimateType === EstimateType.MITIGATION ||
              estimateUploadState?.estimateType === EstimateType.CONTENTS) && (
              <div className="mb-4 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
                <label htmlFor="scopeNotes" className="mb-3 block text-lg font-medium text-gray-800">
                  Upload Files:
                </label>
                <input
                  type="file"
                  id="mobileFileUpload"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const files = Array.from(e.target.files);
                      const fileType =
                        estimateUploadState?.estimateType === EstimateType.MITIGATION
                          ? EstimateFile.MITIGATION_REPORT
                          : EstimateFile.CONTENTS_TEMPLATE;
                      uploadMultipleFiles(files, fileType, "", estimateUploadState?.tpa ?? "", estimateUploadState?.insuranceCarrier ?? "");
                    }
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="mobileFileUpload"
                  className="block w-full cursor-pointer rounded-lg bg-gray-100 p-4 text-center text-gray-700 transition-colors hover:bg-gray-200"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Paperclip size={20} className="text-gray-600" />
                      <span>Tap here to upload files</span>
                    </div>
                    <span className="text-xs text-gray-500">Images, PDFs, and documents</span>
                  </div>
                </label>

                {/* Display uploaded files */}

                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file) => (
                    <UploadedFile
                      key={file?.uploadId}
                      file={file}
                      onRemove={removeUploadedFile}
                      validation={file.uploadId ? pdfValidationState[file.uploadId] : undefined}
                    />
                  ))}
                  {Object.values(uploadProgress).map((item, index) => (
                    <UploadedFileProgress key={index} name={item.fileName} progress={item.progress} />
                  ))}
                </div>
                {!!uploadValidationWarningMessage && <p className="mt-2 text-sm text-amber-700">{uploadValidationWarningMessage}</p>}
              </div>
            )}

          <div className="mb-8 w-full flex-shrink-0 rounded-lg bg-white p-4 transition-all duration-300 ease-in-out">
            <label htmlFor="scopeNotes" className="mb-3 block text-lg font-medium text-gray-800">
              Scope Notes:
            </label>
            <div className="relative">
              <textarea
                id="scopeNotes"
                value={scopeNoteText}
                onChange={(e) => {
                  _updateScopeNotes({ text: e.target.value });
                  // Auto-resize on value change (for autofill scenarios)
                  const target = e.target as HTMLTextAreaElement;
                  setTimeout(() => {
                    target.style.height = "auto";
                    const newHeight = Math.min(target.scrollHeight, 400);
                    target.style.height = `${newHeight}px`;
                  }, 0);
                }}
                onBlur={() => _updateScopeNotes({ text: scopeNoteText })}
                placeholder="Enter scope notes..."
                className={`'bg-gray-100 focus:ring-blue-500' max-h-[400px] min-h-[200px] w-full resize-none overflow-y-auto rounded-lg bg-gray-100 p-3 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                style={{ height: "auto" }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = "auto";
                  const newHeight = Math.min(target.scrollHeight, 400);
                  target.style.height = `${newHeight}px`;
                }}
              />
              {isLoadingConversationData && (
                <div className="bg-opacity-75 absolute inset-0 flex items-center justify-center rounded-lg bg-white">
                  <div className="flex items-center gap-2 text-gray-600">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                    <span>Loading scope notes...</span>
                  </div>
                </div>
              )}
            </div>
            {estimateUploadState?.estimateType === EstimateType.MITIGATION && (
              <div className="mt-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={estimateUploadState.scopeNoteWorkAfterHours}
                    onChange={(e) => _updateScopeNotes({ workAfterHours: e.target.checked })}
                    className="h-4 w-4 text-black accent-black focus:ring-2 focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">Work Performed After Hours</span>
                </label>
                {isWorkAfterHoursAutoDetected && (
                  <div className="text-xs text-gray-400 italic">
                    {`Automatically selected since it's currently outside of normal working hours of Mon-Fri, 8am-5pm`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Fixed Submit Button */}
      <div className="fixed right-0 bottom-0 left-0 z-20 border-t border-gray-200 bg-white p-4">
        {!!uploadValidationWarningMessage && (
          <div className="mb-2">
            <p className="text-sm text-amber-700">{uploadValidationWarningMessage}</p>
          </div>
        )}
        {!!missingFieldsText && (
          <div className="mb-2">
            <p className={`${missingFieldTextTailwind}`}>{missingFieldsText}</p>
          </div>
        )}
        <div className="flex flex-row gap-4">
          <Button
            className="h-12 w-full rounded-lg border border-gray-300 px-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
            variant="outline"
            onClick={async () => await handleEndConversationAndShowDialog()}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmitButtonClicked}
            className={`h-12 w-full rounded-lg px-6 font-medium transition-all duration-200 ${
              isSubmitButtonDisabled ? "cursor-not-allowed bg-gray-300 text-gray-500" : "!bg-black !text-white hover:bg-gray-800"
            } ${isLoading ? "animate-pulse" : ""}`}
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

const EstimateFileUploadSection = ({
  uploadedFiles,
  uploadProgress,
  pdfValidationState,
  uploadValidationWarningMessage,
  removeUploadedFile,
  uploadMultipleFiles,
  tpa,
  insuranceCarrier,
  fileType,
}: {
  uploadedFiles: any[];
  uploadProgress: UploadProgress;
  pdfValidationState: PdfValidationState;
  uploadValidationWarningMessage?: string;
  removeUploadedFile: (fileId: string) => void;
  uploadMultipleFiles: (files: File[], type: EstimateFile, command: string, tpa: string, carrier: string) => void;
  tpa: string;
  insuranceCarrier: string;
  fileType: EstimateFile;
}) => {
  // Filter files to show only `fileType`
  const files = uploadedFiles.filter((file) => file.fileType === fileType);

  // Filter upload progress to show only `fileType` progress
  const fileUploadProgress = uploadProgress ? Object.values(uploadProgress).filter((item) => item.fileType === fileType) : [];

  let title: string = "";
  if (fileType === EstimateFile.MITIGATION_REPORT) {
    title = "Mitigation Report";
  } else if (fileType === EstimateFile.XACTIMATE_ESTIMATE) {
    title = "Xactimate Estimate";
  } else if (fileType === EstimateFile.CONTENTS_TEMPLATE) {
    title = "Contents Template";
  } else if (fileType === EstimateFile.TIC) {
    title = "Tic Sheet";
  }

  return (
    <div className="w-full">
      <DragAndDropFileUploader
        title={title}
        className="w-full"
        icon={<img src="/upload-document.svg" />}
        onChange={(files) => {
          uploadMultipleFiles(files, fileType, "", tpa, insuranceCarrier);
        }}
      />

      {/* Display uploaded files and progress */}
      {(files.length > 0 || fileUploadProgress.length > 0) && (
        <div className="mt-4 w-full">
          <div className="grid grid-cols-2 gap-2">
            {files.map((file) => (
              <UploadedFile
                key={file?.uploadId}
                file={file}
                onRemove={removeUploadedFile}
                validation={file.uploadId ? pdfValidationState[file.uploadId] : undefined}
              />
            ))}
            {fileUploadProgress.map((item, index) => (
              <UploadedFileProgress key={index} name={item.fileName} progress={item.progress} />
            ))}
          </div>
          {!!uploadValidationWarningMessage && <p className="mt-2 text-sm text-amber-700">{uploadValidationWarningMessage}</p>}
        </div>
      )}
    </div>
  );
};

const ConfirmDiscardDialog = ({
  open,
  onOpenChange,
  onConfirm,
  onKeepEditing,
  estimateType,
  hadFiles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onKeepEditing?: () => void;
  estimateType?: EstimateType;
  hadFiles: boolean;
}) => {
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="!bg-white text-black">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-medium text-gray-800">Are you sure you want to cancel?</AlertDialogTitle>
          <AlertDialogDescription className="mt-2 text-sm text-gray-600">
            This will discard all your changes and uploaded files. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-6 flex gap-2">
          <AlertDialogAction
            data-testid="discard-estimate"
            variant="destructive"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.ESTIMATE_DISCARDED, {
                estimate_type: estimateType,
                had_files: hadFiles,
              });
              onConfirm();
            }}
          >
            Discard
          </AlertDialogAction>
          <AlertDialogCancel
            data-testid="keep-editing-estimate"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.ESTIMATE_CANCEL_ABORTED, {});
              onKeepEditing?.();
            }}
          >
            Keep editing
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
